/**
 * What a live event does to the chats list and whether it notifies
 * (crm/src/public/shared/runner-events.js), as pure functions.
 */
import type { LiveEvent, LiveNotification } from './ChatsDeps'
import { chatFanId, unreadCount, type Raw } from './chatRows'

/** A chat moved by a live event, newest event first in the list. */
export interface LiveChat {
  chat: Raw
  at: number
}

const MOVES: ReadonlySet<LiveEvent['kind']> = new Set(['fan_message', 'ai_sent', 'team_sent'])

/**
 * The chat after a message event: last message replaced by what was said,
 * one more unread for a fan message (unless the person is looking at it).
 * `base` is the chat as last known; null when the fan has no chat on screen.
 */
export function chatAfterEvent(base: Raw | null, ev: LiveEvent, opts: { looking: boolean }): Raw | null {
  if (!MOVES.has(ev.kind)) return null
  const fromFan = ev.kind === 'fan_message'
  const chat = base ?? { withUser: { id: Number(ev.fan_id) || ev.fan_id }, unreadMessagesCount: 0 }
  const snippet = String(ev.payload.snippet ?? '')
  return {
    ...chat,
    lastMessage: {
      text: snippet,
      createdAt: ev.occurred_at,
      fromUser: { id: fromFan ? ev.fan_id : 0 },
      isFromAccount: !fromFan,
      media: ev.payload.has_media && !snippet ? [{}] : [],
    },
    unreadMessagesCount: fromFan && !opts.looking ? unreadCount(chat) + 1 : unreadCount(chat),
  }
}

/** Live chats first (newest event first), then the paged ones not among them. */
export function withLive(paged: Raw[], live: Map<string, LiveChat>): Raw[] {
  const top = [...live.values()].sort((a, b) => b.at - a.at).map((x) => x.chat)
  return [...top, ...paged.filter((c) => !live.has(chatFanId(c)))]
}

const NOTIFY_PREF: Partial<Record<LiveEvent['kind'], string>> = {
  fan_message: 'message',
  tip: 'tip',
  ppv_bought: 'purchase',
  subscription: 'subscription',
}

function mutedKind(pref: string): boolean {
  try {
    return localStorage.getItem(`crm_notify_off_${pref}`) === '1'
  } catch {
    return false
  }
}

type T = (key: string, opts?: Record<string, unknown>) => string

/** The notification for an event, or null (not a notifying kind, or muted). */
export function notificationFor(ev: LiveEvent, t: T, href?: string): LiveNotification | null {
  const pref = NOTIFY_PREF[ev.kind]
  if (!pref || mutedKind(pref)) return null
  const name = String(ev.payload.fan_name || ev.fan_id || t('notify.fan'))
  const amount = Number(ev.payload.amount)
  const money = Number.isFinite(amount) && amount > 0 ? `$${amount}` : ''
  const tag = `${ev.kind}-${ev.fan_id}-${ev.id}`
  const link = href ? { href } : {}
  switch (ev.kind) {
    case 'fan_message':
      return { title: t('notify.message', { name }), body: String(ev.payload.snippet || '') || t('notify.messageBody'), tag, ...link }
    case 'tip':
      return { title: t('notify.tip'), body: t('notify.from', { amount: money, name }), tag, ...link }
    case 'ppv_bought':
      return { title: t('notify.purchase'), body: t('notify.from', { amount: money, name }), tag, ...link }
    case 'subscription':
      return { title: t('notify.subscription'), body: name, tag, ...link }
    default:
      return null
  }
}
